function isPlainObject (v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

/*
  * Clean an object by removing ignored fields and sorting keys
  */
function cleanObject (obj, ignoredFields = [], skipArraySorting = false) {
  if (obj === null || obj === undefined) return obj

  // Order arrays and objects recursively
  if (Array.isArray(obj)) {
    const cleanedArray = obj.map(item => cleanObject(item, ignoredFields, skipArraySorting))

    if (!skipArraySorting && cleanedArray.every(el => isPlainObject(el) && 'id' in el)) {
      return cleanedArray.sort((a, b) => {
        if (a.id < b.id) return -1
        if (a.id > b.id) return 1
        return 0
      })
    }

    if (!skipArraySorting) {
      return cleanedArray.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
    }

    return cleanedArray
  }

  if (isPlainObject(obj)) {
    const copy = {}
    Object.keys(obj)
      .filter(key => !ignoredFields.includes(key))
      .sort()
      .forEach(key => {
        copy[key] = cleanObject(obj[key], ignoredFields, skipArraySorting)
      })
    return copy
  }

  return obj
}

/*
  * Compute a raw diff between two objects
  * The result is a nested object where each leaf is { old: ..., new: ... }
  * If there are no differences, returns undefined
  */
function computeRawDiff (o1, o2) {
  if (JSON.stringify(o1) === JSON.stringify(o2)) return undefined
  if (Array.isArray(o1) && Array.isArray(o2)) {
    return compareArraysById(o1, o2)
  }
  if (Array.isArray(o1) || Array.isArray(o2)) {
    const a1 = Array.isArray(o1) ? o1 : []
    const a2 = Array.isArray(o2) ? o2 : []
    const max = Math.max(a1.length, a2.length)
    const result = []
    for (let i = 0; i < max; i++) {
      const d = computeRawDiff(a1[i], a2[i])
      if (d !== undefined) result.push(d)
      else result.push(null)
    }
    return Object.keys(result).length ? result : undefined
  }

  // Object case
  if (isPlainObject(o1) || isPlainObject(o2)) {
    const keys = new Set([
      ...Object.keys(o1 || {}),
      ...Object.keys(o2 || {})
    ])
    const res = {}

    keys.forEach(key => {
      const d = computeRawDiff(o1 ? o1[key] : undefined, o2 ? o2[key] : undefined)
      if (d !== undefined) res[key] = d
    })

    if (o1 && o1.id !== undefined) {
      res.id = res.id || {}
      res.id.old = o1.id
    }
    if (o2 && o2.id !== undefined) {
      res.id = res.id || {}
      res.id.new = o2.id
    }

    return Object.keys(res).length ? res : undefined
  }

  return { old: o1 === undefined ? null : o1, new: o2 === undefined ? null : o2 }
}

function isLeafDiff (node) {
  return node && typeof node === 'object' && ('old' in node) && ('new' in node)
}

/*
  * Apply a raw diff to build old and new target objects
  * This function will set values in oldTarget and newTarget based on the raw diff
  */
function applyRawDiff (raw, oldTarget, newTarget, originalOld, originalNew) {
  if (!raw) return

  Object.keys(raw).forEach(key => {
    const val = raw[key]

    if (isLeafDiff(val)) {
      oldTarget[key] = val.old
      newTarget[key] = val.new
      return
    }

    const numericKeys = Object.keys(val).every(k => /^\d+$/.test(k))
    const origChildOld = originalOld ? originalOld[key] : undefined
    const origChildNew = originalNew ? originalNew[key] : undefined
    const treatAsArray = numericKeys || Array.isArray(origChildOld) || Array.isArray(origChildNew)

    if (treatAsArray) {
      const arrOld = []
      const arrNew = []
      oldTarget[key] = arrOld
      newTarget[key] = arrNew

      const indices = Object.keys(val)
        .map(n => Number(n))
        .sort((a, b) => a - b)

      indices.forEach(idx => {
        const childVal = val[idx]

        if (isLeafDiff(childVal)) {
          arrOld[idx] = childVal.old
          arrNew[idx] = childVal.new
        } else {
          arrOld[idx] = {}
          arrNew[idx] = {}
          applyRawDiff(
            childVal,
            arrOld[idx], arrNew[idx],
            Array.isArray(origChildOld) ? origChildOld[idx] : undefined,
            Array.isArray(origChildNew) ? origChildNew[idx] : undefined
          )
        }

        const candidateOld = origChildOld && origChildOld[idx]
        const candidateNew = origChildNew && origChildNew[idx]

        if (candidateOld && candidateOld.id !== undefined) {
          arrOld[idx] = arrOld[idx] || {}
          arrOld[idx].id = candidateOld.id
        }
        if (candidateNew && candidateNew.id !== undefined) {
          arrNew[idx] = arrNew[idx] || {}
          arrNew[idx].id = candidateNew.id
        }
      })
    } else {
      // Object case
      oldTarget[key] = {}
      newTarget[key] = {}
      applyRawDiff(val, oldTarget[key], newTarget[key], origChildOld, origChildNew)

      // Add special handling for 'id' field to always show it if present
      if (origChildOld && origChildOld.id !== undefined) {
        oldTarget[key].id = origChildOld.id
      }
      if (origChildNew && origChildNew.id !== undefined) {
        newTarget[key].id = origChildNew.id
      }
    }
  })
}

/**
   * Builds a change log DTO by comparing two objects
   */
function buildChangeLogDto (oldObj, newObj, ignoredFields = []) {
  const sortedOld = sortById(oldObj)
  const sortedNew = sortById(newObj)
  const cleanedOld = cleanObject(sortedOld, ignoredFields, true)
  const cleanedNew = cleanObject(sortedNew, ignoredFields, true)

  const raw = computeRawDiff(cleanedOld, cleanedNew)
  if (!raw) {
    return { equals: true, oldDto: {}, newDto: {} }
  }

  const oldDto = {}
  const newDto = {}
  applyRawDiff(raw, oldDto, newDto, sortedOld, sortedNew)

  return { equals: false, oldDto, newDto }
}

/**
   * Add missing IDs from old or database object into a new diff object.
   *
   * @param {Object} newObj - New object to which IDs will be added.
   * @param {Object} dbObj - Database object used as source for IDs.
   * @returns {Object} Modified newObj with IDs added where applicable.
   */
export function addIdsToDiff (newObj, dbObj) {
  const newObjSorted = sortById(newObj)
  const sortedResult = sortById(dbObj)

  return addIdsToDiffRecursive(newObjSorted, sortedResult)
}

function addIdsToDiffRecursive (newObj, dbObj) {
  if (newObj == null) return newObj

  if (Array.isArray(newObj)) {
    newObj.forEach((item, index) => {
      const dbItem = Array.isArray(dbObj) ? dbObj[index] : undefined
      addIdsToDiff(item, dbItem)
    })
  } else if (isPlainObject(newObj)) {
    if (newObj.id == null && dbObj?.id != null) {
      newObj.id = dbObj.id
    }

    Object.keys(newObj).forEach(key => {
      const dbChild = dbObj?.[key]
      addIdsToDiff(newObj[key], dbChild)
    })
  }

  return newObj
}
function compareArraysById (a1, a2) {
  if (!Array.isArray(a1) || !Array.isArray(a2)) {
    return computeRawDiff(a1, a2)
  }

  const map1 = new Map()
  const map2 = new Map()
  const result = []

  a1.forEach(item => {
    const id = item?.id
    if (id !== undefined) {
      map1.set(id, item)
    }
  })

  a2.forEach(item => {
    const id = item?.id
    if (id !== undefined) {
      map2.set(id, item)
    }
  })

  const allIds = new Set([...map1.keys(), ...map2.keys()])

  for (const id of allIds) {
    const oldItem = map1.get(id)
    const newItem = map2.get(id)

    if (!oldItem && newItem) {
      result.push({ old: null, new: cleanObject(newItem, [], true) })
    } else if (oldItem && !newItem) {
      result.push({ old: cleanObject(oldItem, [], true), new: null })
    } else {
      const diff = computeRawDiff(
        cleanObject(oldItem, [], true),
        cleanObject(newItem, [], true)
      )
      if (diff !== undefined) {
        result.push(diff)
      } else {
        result.push(null)
      }
    }
  }

  const withoutId1 = a1.filter(item => item?.id === undefined)
  const withoutId2 = a2.filter(item => item?.id === undefined)

  const maxWithoutId = Math.max(withoutId1.length, withoutId2.length)
  for (let i = 0; i < maxWithoutId; i++) {
    const diff = computeRawDiff(
      cleanObject(withoutId1[i], [], true),
      cleanObject(withoutId2[i], [], true)
    )
    if (diff !== undefined) {
      result.push(diff)
    } else {
      result.push(null)
    }
  }

  const hasAnyDiff = result.some(item => item !== null)
  return hasAnyDiff ? result : undefined
}

function sortById (obj) {
  if (Array.isArray(obj)) {
    return obj
      .sort((a, b) => {
        const hasIdA = a && Object.prototype.hasOwnProperty.call(a, 'id')
        const hasIdB = b && Object.prototype.hasOwnProperty.call(b, 'id')

        if (hasIdA && hasIdB) {
          const idA = a.id
          const idB = b.id
          if (typeof idA === 'number' && typeof idB === 'number') return idA - idB
          return idA?.toString().localeCompare(idB?.toString())
        }

        if (hasIdA && !hasIdB) return -1
        if (!hasIdA && hasIdB) return 1

        const hasNameA = a && Object.prototype.hasOwnProperty.call(a, 'name')
        const hasNameB = b && Object.prototype.hasOwnProperty.call(b, 'name')

        if (hasNameA && hasNameB) return (a.name ?? '').localeCompare(b.name ?? '')
        if (hasNameA && !hasNameB) return -1
        if (!hasNameA && hasNameB) return 1

        const firstPrimitive = o => {
          if (!o || typeof o !== 'object') return ''
          for (const key in o) {
            const val = o[key]
            if (val == null) continue
            if (['string', 'number', 'boolean'].includes(typeof val)) return val
          }
          return ''
        }

        const valA = firstPrimitive(a)
        const valB = firstPrimitive(b)

        if (typeof valA === 'number' && typeof valB === 'number') return valA - valB
        return valA.toString().localeCompare(valB.toString())
      })
      .map(sortById)
  }

  if (obj && typeof obj === 'object') {
    for (const key of Object.keys(obj)) {
      obj[key] = sortById(obj[key])
    }
  }

  return obj
}

/**
   * Public function to diff two objects, ignoring specified fields.
   * @param {Object} obj1 - First object to compare (Old state).
   * @param {Object} obj2 - Second object to compare (New state).
   * @param {Array<string>} ignoredFields - Array of field names to ignore during comparison.
   * @returns {DiffObject} The diff result.
   */
export function diffObjects (obj1, obj2, ignoredFields = []) {
  return buildChangeLogDto(obj1, obj2, ignoredFields)
}
